/**
 * How the conversation reaches the server, and what happens when it cannot.
 *
 * The transport owns the whole degradation contract: every failure — offline,
 * no key on the instance, quota, a model that would not stay in character —
 * comes back as a *reply in her voice* drawn from the scripted pools, flagged
 * `fallback` so the thread knows not to count it as a model turn. The UI
 * never sees an error state; she has an excuse instead, which is the
 * difference between a feature failing and a ghost having a bad connection
 * to the beyond.
 *
 * An interface rather than a function so the dev lab can hand the thread a
 * mock — canned replies, forced failures — and review the whole surface
 * without an account or a key.
 */

import { ApiError, api } from '../api/client.js'
import { sassLine } from '../sass/engine.js'
import { type AwayState, recordAway, refreshAway } from './away.js'
import { refreshFollowUps } from './followups.js'
import { refreshLines } from './lines.js'
import { refreshPromises } from './promises.js'

export interface ChatTurn {
  who: 'user' | 'her'
  text: string
}

export interface ChatSend {
  message?: string
  audio?: { mimeType: string; base64: string }
}

export interface ChatReplyResult {
  reply: string
  /** What the audio turn said, transcribed — null for typed turns. */
  heard: string | null
  endsConversation: boolean
  rude: boolean
  /** True when the reply came from the scripted pools, not the model. */
  fallback: boolean
  /** She has walked off; the thread should show the door, not a composer. */
  away?: boolean
}

export interface ChatTransport {
  send(input: ChatSend, history: readonly ChatTurn[], turn: number): Promise<ChatReplyResult>
  /** The session ended; whatever she wants to keep, she extracts now. */
  close(history: readonly ChatTurn[]): void
}

interface WireReply {
  reply: string
  heard: string | null
  endsConversation: boolean
  rude: boolean
  remaining: number
  away: AwayState | null
}

/** A pool line, or the one neutral sentence for a register with no pool. */
function pool(event: 'chat_excuse' | 'chat_tired' | 'chat_door'): string {
  return sassLine(event) ?? 'Ryšio nėra. Pabandyk vėliau.'
}

export const apiChatTransport: ChatTransport = {
  async send(input, history, turn) {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return {
        reply: pool('chat_excuse'),
        heard: null,
        endsConversation: false,
        rude: false,
        fallback: true,
      }
    }
    try {
      const wire = await api.post<WireReply>('/api/ai/chat', {
        ...(input.message != null ? { message: input.message } : {}),
        ...(input.audio != null ? { audio: input.audio } : {}),
        history,
        turn,
      })
      // She may have just walked off on this very reply; stamping it here
      // means the door is already shut when the thread re-renders.
      recordAway(wire.away)
      return {
        reply: wire.reply,
        heard: wire.heard,
        endsConversation: wire.endsConversation,
        rude: wire.rude,
        fallback: false,
      }
    } catch (error) {
      if (error instanceof ApiError && error.code === 'chat_away') {
        // A door closed on another device, or a cache that had gone stale.
        void refreshAway()
        return {
          reply: pool('chat_door'),
          heard: null,
          endsConversation: true,
          rude: false,
          fallback: true,
          away: true,
        }
      }
      if (error instanceof ApiError && error.code === 'chat_tired') {
        // The day's budget is spent: she is tired, and tired ends the visit.
        // The server has just shut the door until tomorrow — pull it in so
        // the next visit meets the door instead of replaying this excuse.
        void refreshAway()
        return {
          reply: pool('chat_tired'),
          heard: null,
          endsConversation: true,
          rude: false,
          fallback: true,
        }
      }
      // Everything else — quota, no key, an out-of-character model, a dead
      // network mid-flight — is the same fact from the chair he is sitting
      // in: the line to the beyond is down.
      return {
        reply: pool('chat_excuse'),
        heard: null,
        endsConversation: false,
        rude: false,
        fallback: true,
      }
    }
  },

  close(history) {
    if (history.length === 0) return
    void api
      .post('/api/ai/chat/close', { history })
      .then(() => {
        void refreshPromises()
        void refreshFollowUps()
        void refreshLines()
      })
      .catch(() => {
        // A close that never lands is a session she happens not to remember.
      })
  },
}
